import React, { useCallback } from 'react';

import { Guid } from 'src/application/store/model/guid';

import { ContentSectionWrapper } from '../../components/ContentSection/ContentSectionWrapper';
import { FlatContentSection } from '../ContentBlocksRenderer';
import { useContentWithDelay } from '../hooks/useContentWithDelay';
import { Widget } from '../Widgets/Widget';
import { FourColumnsLayout } from './FourColumns/FourColumnsLayout';
import { LeftSideDominantTwoColumnsLayout } from './LeftSideDominantTwoColumns/LeftSideDominantTwoColumnsLayout';
import { RightSideDominantTwoColumnsLayout } from './RightSideDominantTwoColumns/RightSideDominantTwoColumnsLayout';
import { SingleColumnLayout } from './SingleColumn/SingleColumnLayout';
import { ThreeColumnsLayout } from './ThreeColumns/ThreeColumnsLayout';
import { TwoColumnsLayout } from './TwoColumns/TwoColumnsLayout';
import { ColumnLayout, Widget as WidgetModel } from '@ael/core-api';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';

import './Columns.scss';
import { FroalaEditorsSettings } from 'src/config/configs';

interface ContentSectionProps {
  contentItemId: Guid;
  content: FlatContentSection;
  froalaEditorsSettings: FroalaEditorsSettings;
}

export interface ColumntLayoutProps {
  content: FlatContentSection;
  renderWidget: (widget?: WidgetModel) => React.ReactElement;
}

export const ContentSection = ({ contentItemId, content, froalaEditorsSettings }: ContentSectionProps) => {
  const columnLayout = content.columnLayout;

  const {
    backgroundColor,
    paddingTop,
    paddingBottom,
    displayDelayInSeconds,
    maxWidth: sectionWidth,
    verticalAlignment,
  } = content.properties;
  const { isVisible, ref } = useContentWithDelay(contentItemId, displayDelayInSeconds);

  const renderWidget = useCallback(
    (widget?: WidgetModel) => {
      return (
        <Widget contentItemId={contentItemId} widget={widget} sectionDisplayDelayInSeconds={displayDelayInSeconds} froalaEditorsSettings={froalaEditorsSettings} />
      );
    },
    [displayDelayInSeconds, contentItemId, froalaEditorsSettings]
  );

  const getColumnLayout = useCallback(() => {
    switch (columnLayout) {
      case ColumnLayout.SingleColumn:
        return <SingleColumnLayout content={content} renderWidget={renderWidget} />;
      case ColumnLayout.TwoColumns:
        return <TwoColumnsLayout content={content} renderWidget={renderWidget} />;
      case ColumnLayout.ThreeColumns:
        return <ThreeColumnsLayout content={content} renderWidget={renderWidget} />;
      case ColumnLayout.FourColumns:
        return <FourColumnsLayout content={content} renderWidget={renderWidget} />;
      case ColumnLayout.RightSideDominantTwoColumns:
        return <RightSideDominantTwoColumnsLayout content={content} renderWidget={renderWidget} />;
      case ColumnLayout.LeftSideDominantTwoColumns:
        return <LeftSideDominantTwoColumnsLayout content={content} renderWidget={renderWidget} />;
    }
  }, [content, renderWidget, columnLayout]);

  if (isVisible) {
    return (
      <CSSTransition nodeRef={ref} classNames="fade" timeout={450} appear={true} in={true}>
        <div ref={ref}>
          <ContentSectionWrapper
            backgroundColor={backgroundColor}
            paddingTop={paddingTop}
            paddingBottom={paddingBottom}
          >
            <div
              className={classNames({
                'student-columns__vertical-align--center': verticalAlignment === 'center',
                'student-columns__vertical-align--bottom': verticalAlignment === 'bottom',
                'uno-w-full uno-max-w-75': sectionWidth === 'S',
                'uno-w-full uno-max-w-180': sectionWidth === 'M',
                'student-columns': sectionWidth === 'L',
                'uno-w-full uno-max-w-370': sectionWidth === 'XL',
                'uno-w-full uno-max-w-full': sectionWidth === 'fullwidth',
              })}
            >
              {getColumnLayout()}
            </div>
          </ContentSectionWrapper>
        </div>
      </CSSTransition>
    );
  }
  return null;
};
